import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { LoginCredentials, User } from "@/lib/types";
import { queryClient } from "@/lib/queryClient";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { UserRole } from "@shared/schema";
import { signInWithGoogle, handleGoogleRedirectResult } from "@/lib/firebase";

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<User, Error, LoginCredentials>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<User, Error, any>;
  googleSignInMutation: UseMutationResult<User, Error, void>;
  refetchUser: () => void;
};

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [guestUser, setGuestUser] = useState(null);
  
  // Get current user
  const {
    data: user,
    error,
    isLoading,
    refetch: refetchUser,
  } = useQuery<User | null, Error, User | null>({
    queryKey: ["user"],
    queryFn: async ({ queryKey }) => {
      try {
        const apiUser = await apiRequest("GET", queryKey[0] as string);
        if (apiUser) return apiUser;
        // If no API user, check for guest
        const guestInfo = localStorage.getItem("guest_info");
        if (guestInfo) {
          const guest = JSON.parse(guestInfo);
          guest.isGuest = true;
          setGuestUser(guest);
          return guest;
        }
        return null;
      } catch (err) {
        // On error, also check for guest
        const guestInfo = localStorage.getItem("guest_info");
        if (guestInfo) {
          const guest = JSON.parse(guestInfo);
          guest.isGuest = true;
          setGuestUser(guest);
          return guest;
        }
        return null;
      }
    },
  });
  
  // Helper function to check if user should stay on current page (booking/property flow)
  const shouldStayOnCurrentPage = (userData: User) => {
    if (typeof window === 'undefined') return false;
    
    const currentPath = window.location.pathname;
    
    // Stay on property link pages
    if (currentPath.includes('/link')) {
      return true;
    }
    
    // Stay on property detail pages (booking flow)
    if (currentPath.startsWith('/properties/')) {
      return true;
    }
    
    // Stay on checkout or booking pages
    if (currentPath.includes('/checkout') || currentPath.includes('/booking')) {
      return true;
    }
    
    // Stay on current page for guest users (they're likely in a booking flow)
    if (userData.role === UserRole.GUEST || userData.role?.toLowerCase() === 'guest') {
      return true;
    }
    
    return false;
  };

  // Helper function to redirect to dashboard based on user role
  const redirectToDashboard = (userData: User) => {
    // Don't redirect if user should stay on current page
    if (shouldStayOnCurrentPage(userData)) {
      console.log('[use-auth] Staying on current page for user role:', userData.role);
      return;
    }
    
    if (userData.role === UserRole.MANAGER) {
      window.location.href = "/manager/dashboard";
    } else if (userData.role === UserRole.ADMIN) {
      window.location.href = "/admin/properties";
    } else if (userData.role === UserRole.AGENT) {
      window.location.href = "/agent/dashboard";
    } else {
      // Default dashboard for other roles (but guests should have been caught above)
      window.location.href = "/dashboard";
    }
  };

  // Check for Google redirect result on initial load
  useEffect(() => {
    const checkGoogleRedirect = async () => {
      try {
        const result = await handleGoogleRedirectResult();
        
        if (result && result.user) {
          // Get Firebase user data
          const { user: firebaseUser } = result;
          
          // Send token to our backend to authenticate or create an account
          if (firebaseUser.email) {
            const res = await apiRequest("POST", "auth/google", {
              token: await firebaseUser.getIdToken(),
              email: firebaseUser.email,
              displayName: firebaseUser.displayName || '',
            });
            
            if (res.ok) {
              const userData = await res.json();
              queryClient.setQueryData(["user"], userData);
              toast({
                title: "Google sign-in successful",
                description: "Welcome to StayMiddle!",
              });
              
              // Redirect to appropriate dashboard
              redirectToDashboard(userData);
            }
          }
        }
      } catch (err) {
        console.error("Error handling Google sign-in redirect:", err);
      }
    };
    
    checkGoogleRedirect();
  }, [toast]);

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      return await apiRequest('POST', 'login', credentials);
    },
    onSuccess: (userData: User, _variables, context) => {
      queryClient.setQueryData(["user"], userData);
      toast({
        title: "Login successful",
        description: "Welcome back!",
      });
      redirectToDashboard(userData);
    },
    onError: (error: Error) => {
      console.log("use auth error",error.message);
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async (userData: any) => {
      return await apiRequest('POST', 'register', userData);
    },
    onSuccess: (userData: User, _variables, context) => {
      queryClient.setQueryData(["user"], userData);
      toast({
        title: "Registration successful",
        description: "Your account has been created successfully.",
      });
      redirectToDashboard(userData);
    },
    onError: (error: Error) => {
      toast({
        title: "Registration failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Google Sign-in mutation
  const googleSignInMutation = useMutation({
    mutationFn: async () => {
      const result = await signInWithGoogle();
      if (!result || !result.user) {
        throw new Error("Failed to sign in with Google");
      }
      const { user: firebaseUser } = result;
      if (!firebaseUser.email) {
        throw new Error("Google account doesn't have an associated email");
      }
      // Send token to our backend to authenticate or create an account
      return await apiRequest("POST", "auth/google", {
        token: await firebaseUser.getIdToken(),
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || '',
      });
    },
    onSuccess: (userData: User) => {
      queryClient.setQueryData(["user"], userData);
      toast({
        title: "Google sign-in successful",
        description: "Welcome to StayMiddle!",
      });
      redirectToDashboard(userData);
    },
    onError: (error: Error) => {
      toast({
        title: "Google sign-in failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest('POST', 'logout');
    },
    onSuccess: () => {
      queryClient.setQueryData(["user"], null);
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <AuthContext.Provider
      value={{
        user: user ?? guestUser ?? null, // Prefer API user, fallback to guest
        isLoading,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
        googleSignInMutation,
        refetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}